import MUITable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import MUITableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import { IconButton, TableCell, Tooltip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useMemo } from "react";
import { HeaderGroup, Row, TableBodyPropGetter, TableBodyProps, TablePropGetter, TableProps } from "react-table";

import Helpers from "../../../commons/helpers";
import useStrings from "../../../hooks/useStrings";
import Box from "../../Box";
import CheckBox from "../../CheckBox";
import CustomIcon from "../../CustomIcon";
import Radio from "../../Radio";
import Typography from "../../Typography";
import DataTableBodyCell from "../DataTableBodyCell";
import DataTableHeadCell from "../DataTableHeadCell";

import { DataTableProps } from "..";
import { IActionMenuItem } from "../../../commons/interfaces";
import { Constants } from "../../../constants/constants";
import { GroupCompareType, Mode } from "../../../constants/enums";

export const TableContainer = <T,>({
    mode,
    isLocal,
    isSorted,
    noEndBorder,
    isShowIndex,
    table,
    pageNumber,
    allowCheckbox,
    multiSelected,
    disabledCheckdbox,
    dataSelected,
    isRowSelectable,
    handleOnChangeSelected,
    actionList,
    hideActionMenu,

    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageSize,
    setCurrentItem,
    setOpenMenu,
    tableGroup = [],
}: Pick<
    DataTableProps<T>,
    | "mode"
    | "isLocal"
    | "isSorted"
    | "noEndBorder"
    | "isShowIndex"
    | "table"
    | "pageNumber"
    | "allowCheckbox"
    | "multiSelected"
    | "disabledCheckdbox"
    | "dataSelected"
    | "isRowSelectable"
    | "handleOnChangeSelected"
    | "actionList"
    | "hideActionMenu"
    | "tableGroup"
> & {
    getTableProps: (propGetter?: TablePropGetter<object>) => TableProps;
    getTableBodyProps: (propGetter?: TableBodyPropGetter<object>) => TableBodyProps;
    headerGroups: HeaderGroup<object>[];
    prepareRow: (row: Row<object>) => void;
    rows: Row<object>[];
    page: Row<object>[];
    pageSize: number;
    setOpenMenu: React.Dispatch<any>;
    setCurrentItem: React.Dispatch<any>;
}): JSX.Element => {
    const strings = useStrings();

    const dataRowsByTable = useMemo<any>(() => table.rows || [], [table]);
    const dataColumnsByTable = useMemo<any>(() => table.columns, [table]);

    // A function that sets the sorted value for the table
    const setSortedValue = (column: any) => {
        let sortedValue;
        if (isSorted && column.isSorted) {
            sortedValue = column.isSortedDesc ? "desc" : "asce";
        } else if (isSorted) {
            sortedValue = "none";
        } else {
            sortedValue = false;
        }
        return sortedValue;
    };

    // Menu action value state

    const handleOpenMenu = (event: any, row: any) => {
        setOpenMenu(event.currentTarget);
        setCurrentItem(row);
    };

    const renderItemAction = (item: IActionMenuItem, row: any) => (
        <Tooltip key={item.key} title={item.tooltip || item.title}>
            <IconButton
                disabled={item.disabled}
                onClick={() => {
                    setOpenMenu(false);
                    if (Helpers.isFunction(item.callback)) {
                        item.callback(row);
                    }
                }}
            >
                <CustomIcon icon={item.icon} iconName={item.iconName} disabled={item.disabled} />
            </IconButton>
        </Tooltip>
    );

    const renderListAction = (row: any) => {
        const _actionListTemp: any[] = (Helpers.isFunction(actionList) ? actionList(row) : actionList)?.filter((item) => item !== false) || [];

        if (!_actionListTemp || _actionListTemp.length === 0) {
            return null;
        } else {
            if (_actionListTemp.length <= 3) {
                return (
                    <Box display="flex">
                        {_actionListTemp.map((item: IActionMenuItem) => {
                            if (
                                !Helpers.isNullOrEmpty(item?.hideWhen?.key) &&
                                !Helpers.isNullOrEmpty(row?.[item?.hideWhen?.key]) &&
                                item?.hideWhen?.values?.some((value: any) => row?.[item?.hideWhen?.key] === value)
                            ) {
                                return null;
                            } else {
                                return renderItemAction(item, row);
                            }
                        })}
                    </Box>
                );
            } else {
                return (
                    <Box display="flex">
                        {[_actionListTemp[0], _actionListTemp[1]].map((item) => {
                            if (
                                !Helpers.isNullOrEmpty(item?.hideWhen?.key) &&
                                !Helpers.isNullOrEmpty(row?.[item?.hideWhen?.key]) &&
                                item?.hideWhen?.values?.some((value: any) => row?.[item?.hideWhen?.key] === value)
                            ) {
                                return null;
                            } else {
                                return renderItemAction(item, row);
                            }
                        })}
                        <IconButton onClick={(e) => handleOpenMenu(e, row)}>
                            <CustomIcon iconName="menu" />
                        </IconButton>
                    </Box>
                );
            }
        }
    };

    const isCheckedAll = useMemo(() => {
        let countLength: number = 0;
        (isLocal ? [...(dataRowsByTable || [])] : [...(rows || [])]).forEach((el) => {
            if (Helpers.isFunction(isRowSelectable) && isRowSelectable(isLocal ? el : el.original)) {
            } else {
                countLength = countLength + 1;
            }
        });

        return [...(dataSelected || [])].length > 0 && [...(dataSelected || [])].length === countLength;
    }, [dataSelected]);

    // Check All record
    const handleCheckedAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        let data: any[] = [];
        if (event.target?.checked) {
            if (isLocal) {
                [...(dataRowsByTable || [])].forEach((row: any) => {
                    if (Helpers.isFunction(isRowSelectable) && isRowSelectable(row)) {
                    } else {
                        data.push(`${row.id}`);
                    }
                });
            } else {
                [...(rows || [])].forEach((row: any) => {
                    if (Helpers.isFunction(isRowSelectable) && isRowSelectable(row.original)) {
                    } else {
                        data.push(`${row.original?.id}`);
                    }
                });
            }
        } else {
            data = [];
        }
        Helpers.isFunction(handleOnChangeSelected) && handleOnChangeSelected(data);
    };

    // Check record by id
    const handleCheckedByRecord = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (multiSelected) {
            let data = [...(dataSelected || [])];
            if (event.target?.checked) {
                data.push(event.target?.value);
                Helpers.isFunction(handleOnChangeSelected) && handleOnChangeSelected(data);
            } else {
                const temp = data.filter((el) => el !== event.target?.value);

                Helpers.isFunction(handleOnChangeSelected) && handleOnChangeSelected(temp);
            }
        } else {
            if (event.target?.checked) {
                Helpers.isFunction(handleOnChangeSelected) && handleOnChangeSelected([event.target?.value]);
            }
        }
    };

    const getValueCheckedById = (value: any) => {
        let data = [...(dataSelected || [])];
        const index = data.findIndex((el) => el === value.toString());
        return index !== -1;
    };

    const tableRows = useMemo(() => (isLocal ? page : rows), [isLocal, page, rows]);

    return (
        <MUITableContainer sx={{ boxShadow: "none" }}>
            <MUITable {...getTableProps()}>
                {/* Header Cell */}
                <Box component="thead">
                    {headerGroups.map((headerGroup: any, key: any) => (
                        <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                            {allowCheckbox && (
                                <TableCell
                                    style={{
                                        width: "60px",
                                        borderBottom: "none",
                                        backgroundColor: "#e5e7eb",
                                    }}
                                >
                                    {multiSelected && (
                                        <CheckBox
                                            // indeterminate
                                            color="primary"
                                            onChange={handleCheckedAll}
                                            checked={isCheckedAll || false}
                                            inputProps={{ "aria-label": "select all desserts" }}
                                            disabled={disabledCheckdbox || mode === Mode.View}
                                        />
                                    )}
                                </TableCell>
                            )}
                            {isShowIndex && (
                                <DataTableHeadCell noPad width={"50px"} align="left" sorted={false}>
                                    &ensp;&ensp;&nbsp;{"STT"}
                                </DataTableHeadCell>
                            )}
                            {actionList && !hideActionMenu && mode !== Mode.View && (
                                <DataTableHeadCell width={"100px"} align="left" sorted={false}>
                                    &ensp;{strings.Common.ACTION}
                                </DataTableHeadCell>
                            )}
                            {headerGroup.headers.map((column: any, key: any) => (
                                <DataTableHeadCell
                                    key={key}
                                    sorted={setSortedValue(column)}
                                    width={column.width ? column.width : "auto"}
                                    align={column.align ? column.align : "left"}
                                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                                >
                                    {column.render("Header")}
                                </DataTableHeadCell>
                            ))}
                        </TableRow>
                    ))}
                </Box>
                {/* Body Cell */}
                <TableBody {...getTableBodyProps()}>
                    {tableGroup?.length !== 0 &&
                        (tableGroup || [])?.map((group) => (
                            <>
                                <TableRow>
                                    <TableCell
                                        colSpan={dataColumnsByTable.length + 2}
                                        sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
                                            fontSize: size.sm,
                                            borderBottom: noEndBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
                                        })}
                                    >
                                        <Box>
                                            <Typography variant="button" color="secondary" fontWeight="bold">
                                                {group.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                {tableRows
                                    .filter((row) => {
                                        if (group.compareType === GroupCompareType.Include)
                                            return group.groupValue.includes((row.original as any)[group.groupkey]);
                                        if (group.compareType === GroupCompareType.NotInClude)
                                            return !group.groupValue.includes((row.original as any)[group.groupkey]);
                                    })
                                    .map((row: any, key: any) => {
                                        prepareRow(row);
                                        const valChecked = getValueCheckedById(row.original?.id);

                                        return (
                                            <TableContainerRow
                                                key={row.original?.id}
                                                {...{
                                                    mode,
                                                    noEndBorder,
                                                    isShowIndex,
                                                    pageNumber,
                                                    allowCheckbox,
                                                    multiSelected,
                                                    disabledCheckdbox,
                                                    isRowSelectable,
                                                    actionList,
                                                    hideActionMenu,
                                                    row,
                                                    index: Number(key + 1),
                                                    handleCheckedByRecord,
                                                    renderListAction,
                                                    valChecked,
                                                    pageSize,
                                                }}
                                            />
                                        );
                                    })}
                            </>
                        ))}
                    {tableGroup?.length === 0 &&
                        tableRows.map((row: any, key: any) => {
                            prepareRow(row);
                            const valChecked = getValueCheckedById(row.original?.id);

                            return (
                                <TableContainerRow
                                    key={row.original?.id}
                                    {...{
                                        mode,
                                        noEndBorder,
                                        isShowIndex,
                                        pageNumber,
                                        allowCheckbox,
                                        multiSelected,
                                        disabledCheckdbox,
                                        isRowSelectable,
                                        actionList,
                                        hideActionMenu,
                                        row,
                                        index: Number(key + 1),
                                        handleCheckedByRecord,
                                        renderListAction,
                                        valChecked,
                                        pageSize,
                                    }}
                                />
                            );
                        })}
                    {/* no data */}
                    {dataRowsByTable.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={dataColumnsByTable.length + 2}
                                sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
                                    fontSize: size.sm,
                                    borderBottom: noEndBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
                                })}
                            >
                                <Box textAlign="center">
                                    <Typography variant="button" color="secondary" fontWeight="regular">
                                        {strings.Common.NO_DATA}
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </MUITable>
        </MUITableContainer>
    );
};

const TableContainerRow = <T,>({
    mode,
    noEndBorder,
    isShowIndex,
    pageNumber,
    allowCheckbox,
    multiSelected,
    disabledCheckdbox,
    isRowSelectable,
    actionList,
    pageSize,
    hideActionMenu,
    row,
    index,
    handleCheckedByRecord,
    renderListAction,
    valChecked,
}: Pick<
    DataTableProps<T>,
    | "mode"
    | "noEndBorder"
    | "isShowIndex"
    | "pageNumber"
    | "allowCheckbox"
    | "multiSelected"
    | "disabledCheckdbox"
    | "isRowSelectable"
    | "actionList"
    | "hideActionMenu"
> & {
    row: any;
    index: number;
    pageSize: number;
    handleCheckedByRecord: (event: React.ChangeEvent<HTMLInputElement>) => void;
    renderListAction: (row: any) => JSX.Element;
    valChecked: boolean;
}) => {
    return (
        <TableRow {...row.getRowProps()}>
            {allowCheckbox && (
                <TableCell
                    padding="checkbox"
                    sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
                        fontSize: size.sm,
                        borderBottom: noEndBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
                    })}
                >
                    {multiSelected ? (
                        <CheckBox
                            color="primary"
                            checked={valChecked}
                            value={row.original?.id}
                            onChange={handleCheckedByRecord}
                            inputProps={{ "aria-label": "select all desserts" }}
                            disabled={Helpers.isFunction(isRowSelectable) ? isRowSelectable(row.original) : disabledCheckdbox || mode === Mode.View}
                        />
                    ) : (
                        <Radio
                            color="primary"
                            checked={valChecked}
                            value={row.original?.id}
                            onChange={handleCheckedByRecord}
                            inputProps={{ "aria-label": "select all desserts" }}
                            disabled={Helpers.isFunction(isRowSelectable) ? isRowSelectable(row.original) : disabledCheckdbox || mode === Mode.View}
                        />
                    )}
                </TableCell>
            )}
            {isShowIndex && (
                <TableCell
                    padding="checkbox"
                    sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
                        fontSize: size.sm,
                        borderBottom: noEndBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
                    })}
                >
                    <Typography
                        sx={{
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: "#7b809a",
                            fontSize: "0.75rem",
                            letterSpacing: "0.03333em",
                        }}
                    >
                        {index + ((pageNumber || 1) - 1) * (pageSize || Constants.ROW_PER_PAGE)}
                    </Typography>
                </TableCell>
            )}
            {actionList && !hideActionMenu && mode !== Mode.View && (
                <TableCell
                    padding="checkbox"
                    sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
                        fontSize: size.sm,
                        borderBottom: noEndBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
                    })}
                >
                    {renderListAction(row.original)}
                </TableCell>
            )}
            {row.cells.map((cell: any, key: any) => (
                <DataTableBodyCell
                    key={key}
                    noBorder={noEndBorder}
                    noPad={cell.column.noPad}
                    width={cell.column.width}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                >
                    {cell.render("Cell")}
                </DataTableBodyCell>
            ))}
        </TableRow>
    );
};
export default TableContainer;
